import React from 'react'
import styled, { labelWhenSmallProps } from '../../constants/theme'
import { SelectorIcon } from '../elements/Icons'
import { FormElementProps, RightIconContainer } from './FormElementParts'

const getHourOptions = (value, minutes) => {
  if (minutes > 0) {
    return allHours(value)
  } else return twentyThreeHours(value)
}

const allHours = (value) =>
  Array(24)
    .fill('')
    .map((_, i) => {
      const hourVal = `${i}`
      return (
        <option
          aria-selected={`${value}` === hourVal ? true : false}
          key={hourVal}
          value={i}
        >
          {hourVal}
        </option>
      )
    })

const twentyThreeHours = (value) =>
  Array(23)
    .fill('')
    .map((_, i) => {
      const hourVal = `${i + 1}`
      return (
        <option
          aria-selected={`${value}` === hourVal ? true : false}
          key={hourVal}
          value={hourVal}
        >
          {hourVal}
        </option>
      )
    })

const getMinuteOptions = (value, hours) => {
  if (hours !== 0) {
    return allMinutes(value)
  } else return quarterTime(value)
}

const allMinutes = (value) =>
  Array(4)
    .fill('')
    .map((_, i) => {
      const minVal = `${i * 15}`
      return (
        <option
          aria-selected={`${value}` === minVal ? true : false}
          key={minVal}
          value={minVal}
        >
          {minVal}
        </option>
      )
    })

const quarterTime = (value) =>
  Array(3)
    .fill('')
    .map((_, i) => {
      const minVal = `${(i + 1) * 15}`
      return (
        <option
          aria-selected={`${value}` === minVal ? true : false}
          key={minVal}
          value={minVal}
        >
          {minVal}
        </option>
      )
    })

const SECONDS_IN_A_MINUTE = 60
const SECONDS_IN_AN_HOUR = SECONDS_IN_A_MINUTE * 60
const SECONDS_IN_8_HOURS = SECONDS_IN_AN_HOUR * 8

const DurationElement: React.FC<FormElementProps> = ({
  name,
  onChange,
  value,
}) => {
  const [state, setState] = React.useState({ hours: 8, minutes: 0 })
  const { hours, minutes } = state

  const handleChange = React.useCallback(
    (e) => {
      e.preventDefault()
      const newValue = parseInt(e.target.value, 10)
      const newState = {
        ...state,
        [e.target.name]: newValue,
      }
      setState(newState)
      if (onChange) {
        onChange({
          name,
          value:
            newState.hours * SECONDS_IN_AN_HOUR +
            newState.minutes * SECONDS_IN_A_MINUTE,
        })
      }
    },
    [name, onChange, state],
  )

  React.useEffect(() => {
    const seconds: any = Number(value || SECONDS_IN_8_HOURS)
    const newHours = Math.floor(seconds / SECONDS_IN_AN_HOUR)
    setState({
      hours: newHours,
      minutes: (seconds - newHours * SECONDS_IN_AN_HOUR) / SECONDS_IN_A_MINUTE,
    })
  }, [value])

  return (
    <FlexContainer>
      <SelectContainer>
        <Select
          id="hours"
          name="hours"
          onChange={handleChange}
          value={`${hours}`}
        >
          {getHourOptions(hours, minutes)}
        </Select>
        <RightIconContainer>
          <SelectorIcon />
        </RightIconContainer>
        <Label htmlFor="hours">Hours</Label>
      </SelectContainer>
      <SelectContainer>
        <Select
          id="minutes"
          name="minutes"
          onChange={handleChange}
          value={`${minutes}`}
        >
          {getMinuteOptions(minutes, hours)}
        </Select>
        <RightIconContainer>
          <SelectorIcon />
        </RightIconContainer>
        <Label htmlFor="minutes">Minutes</Label>
      </SelectContainer>
    </FlexContainer>
  )
}

export const FlexContainer = styled.div({
  '& > div:last-child': {
    marginRight: 0,
  },
  display: 'flex',
})

export const SelectContainer = styled.div(
  {
    '& label': {
      ...labelWhenSmallProps,
    },
    display: 'inline-block',
    flexGrow: 1,
    marginRight: 10,
    position: 'relative',
  },
  ({ theme }) => ({
    fontFamily: theme.fontFamily,
  }),
)

const Label = styled.label<{ icon?: JSX.Element }>(
  {
    fontSize: 16,
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    transition: 'all 0.2s ease',
  },
  ({ icon, theme }) => ({
    color: theme.text,
    fontFamily: theme.fontFamily,
    marginLeft: icon ? 30 : 0,
  }),
)

const Select = styled.select(
  {
    '&::-ms-expand': {
      display: 'none',
    },
    '&:disabled': {
      cursor: 'not-allowed',
    },
    appearance: 'none',
    display: 'inline-block',
    width: '100%',
  },
  ({ theme }) => ({
    border: `1px solid ${theme.border}`,
    borderRadius: theme.borderRadiusSmall,
    color: theme.text,
    fontFamily: theme.fontFamily,
    padding: theme.paddingSmall,

    '&:focus': {
      border: `1px solid ${theme.primary}`,
      boxShadow: `0 0 0 1px ${theme.primary}`,
    },
  }),
)
export default DurationElement
